// The agent room header (M2-06, M4-05; spec 06 §Channels "Agent rooms"; agents-in-chat research
// recs. 1, 11–12): "Agent room — n agents, watching", the participants with their badges ("DE ·
// Recruiting coordinator", "Jordan's agent"), whose turn it is ("Paused after 8 agent turns —
// post to continue" once the agents have talked among themselves long enough), Join / Leave,
// Add participant, Take over / Release, and the status asks escalated to the viewer. Taking over
// pauses the agents' posting and parks their runs; the room shows a red banner until it is
// released, and a release can leave a hand-back note the agents read on their next turn.
// Without the agents add-on there are no room rows to read: the header names the agents seen
// in the room and Join is the channel setting.
import {
  Alert,
  AlertDescription,
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertTitle,
  Badge,
  Button,
  Checkbox,
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Field,
  FieldLabel,
  Input,
  Notice,
  Textarea,
  Tooltip,
  TooltipContent,
  TooltipTrigger,
  toast,
  WorkerBadge,
  workerName,
} from "@companyos/ui";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  BotIcon,
  HandIcon,
  LogInIcon,
  LogOutIcon,
  MessageCircleQuestionIcon,
  PlayIcon,
  Trash2Icon,
  UserPlusIcon,
} from "lucide-react";
import { type FormEvent, useState } from "react";
import { myCrewQuery } from "../queries";
import { useManagesChannel } from "./channel-agents";
import { usePrefs } from "./channel-settings";
import { type Candidate, roomCandidates, runStateLabel, turnLine } from "./chat-model";
import {
  type AgentRoomView,
  asksQuery,
  type Channel,
  chatKeys,
  clientId,
  type RoomParticipant,
  type Run,
  type StatusAsk,
} from "./chat-queries";
import { useAgentRoom } from "./conversation";
import type { ChatContext } from "./use-chat";

export type RoomAgent = { id: string; name: string; run?: Run | undefined };

type RunLike = { state: Run["state"] } | null | undefined;

const runVariant = (run: RunLike) =>
  run?.state === "running"
    ? "info"
    : run?.state === "awaiting_approval"
      ? "warning"
      : run?.state === "waiting_input"
        ? "danger"
        : "neutral";

export function AgentRoomHeader({
  chat,
  channel,
  agents,
  noteThreadId,
}: {
  chat: ChatContext;
  channel: Channel;
  /** The agents seen in the room (authors and runs), with their latest live run. */
  agents: readonly RoomAgent[];
  /** Where a release's hand-back note is posted (the open topic). */
  noteThreadId: string | null;
}) {
  const { api, co } = chat;
  const queryClient = useQueryClient();
  const prefs = usePrefs(chat, channel);
  const { room, loaded } = useAgentRoom(chat, channel);
  const [releasing, setReleasing] = useState(false);
  const [adding, setAdding] = useState(false);
  const takenBy = channel.taken_over_by_membership_id;
  const byMe = takenBy !== null && takenBy === chat.meId;
  const refresh = () =>
    Promise.all([
      queryClient.invalidateQueries({ queryKey: chatKeys.channel(co, channel.id) }),
      queryClient.invalidateQueries({ queryKey: chatKeys.channels(co) }),
      queryClient.invalidateQueries({ queryKey: chatKeys.runs(co) }),
      queryClient.invalidateQueries({ queryKey: chatKeys.room(co, channel.id) }),
    ]);
  const takeOver = useMutation({
    mutationFn: () => api.client.channels.takeOver({ params: { co, id: channel.id } }),
    onSuccess: async (c) => {
      queryClient.setQueryData(chatKeys.channel(co, channel.id), c);
      await refresh();
      toast.add({ title: "You took over the room. Agents are paused.", type: "success" });
    },
  });
  const setRoom = (r: AgentRoomView) => queryClient.setQueryData(chatKeys.room(co, channel.id), r);
  const join = useMutation({
    mutationFn: (on: boolean) =>
      on
        ? api.client.agentRooms.join({ params: { co, id: room?.room.id ?? "" } })
        : api.client.agentRooms.leave({ params: { co, id: room?.room.id ?? "" } }),
    onSuccess: (r, on) => {
      setRoom(r);
      toast.add({ title: on ? "You joined the room." : "You left the room.", type: "success" });
    },
  });
  // A channel flagged as a room before M4-05 has no turn-taking yet; anyone in it can start it.
  const setUp = useMutation({
    mutationFn: () =>
      api.client.agentRooms.create({ params: { co }, body: { channel_id: channel.id } }),
    onSuccess: (r) => {
      setRoom(r);
      setAdding(true);
    },
  });
  const canAct = chat.meId !== null && !chat.readOnly;
  const roomAgents = room?.participants.filter((p) => p.kind === "agent");
  const count = roomAgents ? roomAgents.length : agents.length;
  const turn = room ? turnLine(room) : null;
  const inRoom = room?.participants.some((p) => p.membership_id === chat.meId) ?? false;
  const manages = useManagesChannel(chat, channel, false);
  return (
    <div
      data-slot="agent-room"
      className="flex flex-col gap-2 border-b border-border-subtle px-6 py-2.5 max-md:px-3"
    >
      <div className="flex flex-wrap items-center gap-x-3 gap-y-2">
        <span className="inline-flex items-center gap-2 text-sm font-medium text-foreground">
          <BotIcon aria-hidden="true" className="size-4 text-primary-text" />
          Agent room — {count} {count === 1 ? "agent" : "agents"}, {takenBy ? "paused" : "watching"}
        </span>
        {room ? (
          <ParticipantChips participants={room.participants} />
        ) : (
          <ul
            aria-label="Agents in the room"
            className="m-0 flex list-none flex-wrap items-center gap-1.5 p-0"
          >
            {agents.map((a) => (
              <li key={a.id}>
                <Badge variant={runVariant(a.run)} dot>
                  {workerName(a.name)} ·{a.run ? runStateLabel(a.run) : "idle"}
                </Badge>
              </li>
            ))}
          </ul>
        )}
        {turn ? (
          <Badge
            data-slot="room-turn"
            data-stalled={turn.stalled || undefined}
            variant={turn.stalled ? "warning" : "accent"}
          >
            {turn.text}
          </Badge>
        ) : null}
        {canAct ? (
          <div className="ml-auto flex flex-wrap items-center gap-2">
            {room ? (
              <>
                <Button size="sm" variant="secondary" onClick={() => setAdding(true)}>
                  <UserPlusIcon data-icon="inline-start" />
                  Add participant
                </Button>
                <Button
                  size="sm"
                  variant={inRoom ? "ghost" : "secondary"}
                  disabled={join.isPending}
                  onClick={() => join.mutate(!inRoom)}
                >
                  {inRoom ? (
                    <LogOutIcon data-icon="inline-start" />
                  ) : (
                    <LogInIcon data-icon="inline-start" />
                  )}
                  {inRoom ? "Leave" : "Join"}
                </Button>
                {manages ? <CloseRoomButton chat={chat} room={room} onClosed={refresh} /> : null}
              </>
            ) : chat.agents ? (
              loaded ? (
                <Button
                  size="sm"
                  variant="secondary"
                  disabled={setUp.isPending}
                  onClick={() => setUp.mutate()}
                >
                  <UserPlusIcon data-icon="inline-start" />
                  Set up turns
                </Button>
              ) : null
            ) : prefs.loaded && !prefs.joined ? (
              <Button
                size="sm"
                variant="secondary"
                onClick={() => prefs.save.mutate({ joined: true })}
              >
                <LogInIcon data-icon="inline-start" />
                Join
              </Button>
            ) : null}
            {takenBy ? (
              byMe ? (
                <Button size="sm" onClick={() => setReleasing(true)}>
                  <PlayIcon data-icon="inline-start" />
                  Release
                </Button>
              ) : null
            ) : (
              <Button
                size="sm"
                variant="destructive"
                disabled={takeOver.isPending}
                onClick={() => takeOver.mutate()}
              >
                <HandIcon data-icon="inline-start" />
                Take over
              </Button>
            )}
          </div>
        ) : null}
      </div>
      {takenBy ? (
        <Alert variant="destructive" data-slot="taken-over">
          <HandIcon />
          <AlertTitle>
            {byMe
              ? "You have taken over this room"
              : `${chat.members.get(takenBy)?.name ?? "Someone"} has taken over this room`}
          </AlertTitle>
          <AlertDescription>
            Agents do not post here and their runs are parked until the room is released.
          </AlertDescription>
        </Alert>
      ) : null}
      {room && room.open_status_asks > 0 && chat.meId ? (
        <EscalatedAsks chat={chat} room={room} />
      ) : null}
      {releasing ? (
        <ReleaseDialog
          chat={chat}
          channel={channel}
          noteThreadId={noteThreadId}
          onDone={refresh}
          onClose={() => setReleasing(false)}
        />
      ) : null}
      {adding && room ? (
        <AddParticipantDialog
          chat={chat}
          room={room}
          onAdded={setRoom}
          onClose={() => setAdding(false)}
        />
      ) : null}
    </div>
  );
}

/**
 * ACTIONS: "Close room" (the channel's managers; `DELETE agent-rooms/{id}`). The messages stay: a
 * room channel of its own is archived, a channel flagged a room becomes a plain one. The API
 * refuses a room that is taken over or has status asks (its record), and says so.
 */
function CloseRoomButton({
  chat,
  room,
  onClosed,
}: {
  chat: ChatContext;
  room: AgentRoomView;
  onClosed: () => Promise<unknown>;
}) {
  const [open, setOpen] = useState(false);
  const close = useMutation({
    mutationFn: () =>
      chat.api.client.agentRooms.delete({ params: { co: chat.co, id: room.room.id } }),
    onSuccess: async () => {
      setOpen(false);
      await onClosed();
      toast.add({ title: "Room closed. Its messages are kept.", type: "success" });
    },
    onError: () => setOpen(false),
  });
  return (
    <>
      <Tooltip>
        <TooltipTrigger
          render={
            <Button
              size="icon-sm"
              variant="ghost"
              aria-label={`Close the room ${room.name}`}
              className="text-muted-foreground hover:text-danger-text"
              onClick={() => setOpen(true)}
            />
          }
        >
          <Trash2Icon aria-hidden={true} />
        </TooltipTrigger>
        <TooltipContent>Close room</TooltipContent>
      </Tooltip>
      <AlertDialog open={open} onOpenChange={setOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Close the room “{room.name}”?</AlertDialogTitle>
            <AlertDialogDescription>
              Turn-taking ends and its participants leave the room. The messages are kept.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Keep it</AlertDialogCancel>
            <AlertDialogAction
              variant="destructive"
              disabled={close.isPending}
              onClick={() => close.mutate()}
            >
              Close room
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}

/** Each participant with its badge; the turn holder is marked, an agent shows its run state. */
function ParticipantChips({ participants }: { participants: readonly RoomParticipant[] }) {
  return (
    <ul aria-label="Participants" className="m-0 flex list-none flex-wrap items-center gap-1.5 p-0">
      {participants.map((p) => (
        <li
          key={p.membership_id}
          data-slot="room-participant"
          data-kind={p.badge.kind}
          data-turn={p.is_turn || undefined}
        >
          <Badge
            variant={p.is_turn ? "accent" : p.kind === "agent" ? runVariant(p.run) : "outline"}
            dot={p.kind === "agent"}
          >
            {p.badge.kind === "de" || p.badge.kind === "crew" ? (
              <WorkerBadge kind={p.badge.kind === "de" ? "de" : "ag"} size="sm" />
            ) : null}
            {workerName(p.display_name)}
            {p.kind === "agent" && (p.badge.label !== p.display_name || p.run?.parked) ? (
              <span className="text-muted-foreground">
                {p.badge.label !== p.display_name ? ` · ${workerName(p.badge.label)}` : ""}
                {p.run?.parked ? " · parked" : ""}
              </span>
            ) : null}
          </Badge>
        </li>
      ))}
    </ul>
  );
}

/**
 * Who can be added: DEs, the viewer's own crew (`crews.me`, read when the dialog opens), people.
 * Another person's crew is not offered; its owner brings it in.
 */
function AddParticipantDialog({
  chat,
  room,
  onAdded,
  onClose,
}: {
  chat: ChatContext;
  room: AgentRoomView;
  onAdded: (r: AgentRoomView) => void;
  onClose: () => void;
}) {
  const { api, co } = chat;
  const crew = useQuery({ ...myCrewQuery(api, co), retry: false });
  const mine = crew.data
    ? { id: crew.data.crew.membership_id, name: crew.data.agent.display_name }
    : null;
  const candidates = roomCandidates(room, chat.members, chat.meId, mine);
  const [picked, setPicked] = useState<string[]>([]);
  const add = useMutation({
    mutationFn: async () => {
      let last = room;
      for (const id of picked)
        last = await api.client.agentRooms.addParticipant({
          params: { co, id: room.room.id },
          body: { membership_id: id },
        });
      return last;
    },
    onSuccess: (r) => {
      onAdded(r);
      toast.add({
        title: picked.length === 1 ? "Added to the room." : `Added ${picked.length} to the room.`,
        type: "success",
      });
      onClose();
    },
  });
  const submit = (e: FormEvent) => {
    e.preventDefault();
    if (picked.length) add.mutate();
  };
  return (
    <Dialog open onOpenChange={(o) => !o && !add.isPending && onClose()}>
      <DialogContent>
        <form onSubmit={submit} className="flex flex-col gap-5">
          <DialogHeader>
            <DialogTitle>Add participants</DialogTitle>
            <DialogDescription>
              Agents take turns in the order they join. People can post any time; a post restarts
              the agents' turn count.
            </DialogDescription>
          </DialogHeader>
          <ParticipantPicker
            idPrefix="add-participant"
            candidates={candidates}
            value={picked}
            onChange={setPicked}
          />
          <DialogFooter>
            <DialogClose render={<Button variant="ghost" type="button" />}>Cancel</DialogClose>
            <Button type="submit" disabled={!picked.length || add.isPending}>
              Add
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

/** A checkbox per candidate: DEs and the viewer's crew first, then people. */
export function ParticipantPicker({
  idPrefix,
  candidates,
  value,
  onChange,
}: {
  idPrefix: string;
  candidates: readonly Candidate[];
  value: readonly string[];
  onChange: (ids: string[]) => void;
}) {
  if (!candidates.length)
    return <p className="m-0 text-sm text-muted-foreground">Everyone is already in the room.</p>;
  return (
    <fieldset
      aria-label="Participants"
      className="m-0 flex max-h-72 flex-col gap-2.5 overflow-y-auto border-0 p-0"
    >
      {candidates.map((c) => (
        <Field key={c.id} orientation="horizontal">
          <Checkbox
            id={`${idPrefix}-${c.id}`}
            checked={value.includes(c.id)}
            onCheckedChange={(on) =>
              onChange(on ? [...value, c.id] : value.filter((x) => x !== c.id))
            }
          />
          <FieldLabel htmlFor={`${idPrefix}-${c.id}`} className="font-normal">
            {c.kind === "de" || c.kind === "crew" ? (
              <WorkerBadge kind={c.kind === "de" ? "de" : "ag"} size="sm" className="mr-1" />
            ) : null}
            {workerName(c.name)}
            <span className="ml-1 text-muted-foreground">· {c.hint}</span>
          </FieldLabel>
        </Field>
      ))}
    </fieldset>
  );
}

/**
 * Status asks escalated to the viewer: an agent asked their crew for a task's status and the
 * crew's sharing rule kept it, or nobody answered in time. The person answers or declines.
 */
function EscalatedAsks({ chat, room }: { chat: ChatContext; room: AgentRoomView }) {
  const { api, co } = chat;
  const asks = useQuery({ ...asksQuery(api, co, room.room.id), retry: false });
  const [answering, setAnswering] = useState<StatusAsk | null>(null);
  const queryClient = useQueryClient();
  const done = () =>
    Promise.all([
      queryClient.invalidateQueries({ queryKey: chatKeys.asks(co, room.room.id) }),
      queryClient.invalidateQueries({ queryKey: chatKeys.room(co, room.room.channel_id) }),
    ]);
  const decline = useMutation({
    mutationFn: (id: string) => api.client.statusAsks.decline({ params: { co, id }, body: {} }),
    onSuccess: async () => {
      await done();
      toast.add({ title: "Declined. The asker is told to ask you directly.", type: "success" });
    },
  });
  const mine = (asks.data?.data ?? []).filter((a) => a.escalated_to_membership_id === chat.meId);
  // An ask escalates to the target crew's owner: a target nobody here lists is the viewer's own.
  const nameOf = (id: string, fallback: string) =>
    workerName(
      room.participants.find((p) => p.membership_id === id)?.display_name ??
        chat.members.get(id)?.name ??
        fallback,
    );
  if (!mine.length) return null;
  return (
    <>
      {mine.map((a) => (
        <Notice
          key={a.id}
          tone="warning"
          icon={MessageCircleQuestionIcon}
          data-slot="status-ask"
          title={`${nameOf(a.asker_membership_id, "An agent")} asked ${nameOf(a.target_membership_id, "your agent")} for a status`}
          action={
            chat.readOnly ? null : (
              <span className="flex gap-2">
                <Button size="sm" onClick={() => setAnswering(a)}>
                  Answer
                </Button>
                <Button
                  size="sm"
                  variant="ghost"
                  disabled={decline.isPending}
                  onClick={() => decline.mutate(a.id)}
                >
                  Decline
                </Button>
              </span>
            )
          }
        >
          {a.question ? `“${a.question}” ` : ""}
          {a.escalation === "stuck"
            ? "Nobody answered in time, so it came to you."
            : "Your agent's sharing rule keeps this to you, so it came to you."}
        </Notice>
      ))}
      {answering ? (
        <AnswerDialog
          chat={chat}
          ask={answering}
          onDone={done}
          onClose={() => setAnswering(null)}
        />
      ) : null}
    </>
  );
}

function AnswerDialog({
  chat,
  ask,
  onDone,
  onClose,
}: {
  chat: ChatContext;
  ask: StatusAsk;
  onDone: () => Promise<unknown>;
  onClose: () => void;
}) {
  const [status, setStatus] = useState("");
  const [note, setNote] = useState("");
  const answer = useMutation({
    mutationFn: () =>
      chat.api.client.statusAsks.answer({
        params: { co: chat.co, id: ask.id },
        body: { status: status.trim(), ...(note.trim() ? { note: note.trim() } : {}) },
      }),
    onSuccess: async () => {
      await onDone();
      toast.add({ title: "Answered. The room sees your status.", type: "success" });
      onClose();
    },
  });
  const submit = (e: FormEvent) => {
    e.preventDefault();
    if (status.trim()) answer.mutate();
  };
  return (
    <Dialog open onOpenChange={(o) => !o && !answer.isPending && onClose()}>
      <DialogContent>
        <form onSubmit={submit} className="flex flex-col gap-5">
          <DialogHeader>
            <DialogTitle>Answer the status ask</DialogTitle>
            <DialogDescription>
              A short status goes to the room; the note is optional.
            </DialogDescription>
          </DialogHeader>
          <Field>
            <FieldLabel htmlFor="ask-status">Status</FieldLabel>
            <Input
              id="ask-status"
              className="h-10"
              maxLength={60}
              placeholder="e.g. Sent Tuesday"
              value={status}
              onChange={(e) => setStatus(e.target.value)}
            />
          </Field>
          <Field>
            <FieldLabel htmlFor="ask-note">Note (optional)</FieldLabel>
            <Textarea
              id="ask-note"
              rows={2}
              value={note}
              onChange={(e) => setNote(e.target.value)}
            />
          </Field>
          <DialogFooter>
            <DialogClose render={<Button variant="ghost" type="button" />}>Cancel</DialogClose>
            <Button type="submit" disabled={!status.trim() || answer.isPending}>
              Answer
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function ReleaseDialog({
  chat,
  channel,
  noteThreadId,
  onDone,
  onClose,
}: {
  chat: ChatContext;
  channel: Channel;
  noteThreadId: string | null;
  onDone: () => Promise<unknown>;
  onClose: () => void;
}) {
  const { api, co } = chat;
  const queryClient = useQueryClient();
  const [note, setNote] = useState("");
  const release = useMutation({
    mutationFn: async () => {
      if (note.trim() && noteThreadId)
        await api.client.messages.create({
          params: { co, id: noteThreadId },
          body: { client_id: clientId(), body_markdown: `Hand-back: ${note.trim()}` },
        });
      return api.client.channels.release({ params: { co, id: channel.id } });
    },
    onSuccess: async (c) => {
      queryClient.setQueryData(chatKeys.channel(co, channel.id), c);
      if (noteThreadId)
        void queryClient.invalidateQueries({ queryKey: chatKeys.messages(co, noteThreadId) });
      await onDone();
      toast.add({ title: "Released. Agents pick up where they left off.", type: "success" });
      onClose();
    },
  });
  const submit = (e: FormEvent) => {
    e.preventDefault();
    release.mutate();
  };
  return (
    <Dialog open onOpenChange={(o) => !o && !release.isPending && onClose()}>
      <DialogContent>
        <form onSubmit={submit} className="flex flex-col gap-5">
          <DialogHeader>
            <DialogTitle>Release the room</DialogTitle>
            <DialogDescription>
              Parked runs resume. The agents read what people said while you had the room, and your
              note if you leave one.
            </DialogDescription>
          </DialogHeader>
          {noteThreadId ? (
            <Field>
              <FieldLabel htmlFor="hand-back">Hand-back note (optional)</FieldLabel>
              <Textarea
                id="hand-back"
                rows={3}
                value={note}
                placeholder="e.g. I told Bo we'd call Friday; carry on with the others."
                onChange={(e) => setNote(e.target.value)}
              />
            </Field>
          ) : null}
          <DialogFooter>
            <DialogClose render={<Button variant="ghost" type="button" />}>Cancel</DialogClose>
            <Button type="submit" disabled={release.isPending}>
              Release
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
